/**
 * Webhook Test Modal Component
 * Test webhook by sending a sample message
 */

import React, { useState } from 'react';
import { apiClient } from '../../api/client';

interface Webhook {
  id: number;
  name: string;
  webhookUrl: string;
  eventType: string;
}

interface WebhookTestModalProps {
  webhook: Webhook;
  onClose: () => void;
}

export const WebhookTestModal: React.FC<WebhookTestModalProps> = ({
  webhook,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleTest = async () => {
    try {
      setLoading(true);
      setResult(null);

      const response = await apiClient.post(
        `/api/v1/admin/webhooks/${webhook.id}/test`
      );

      setResult({
        success: true,
        message: response.data.message || 'Test webhook sent successfully!',
      });
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to send test webhook',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800/80 rounded-lg max-w-md w-full border border-slate-700/50">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Test Webhook</h2>

          <div className="space-y-4">
            <div>
              <div className="text-slate-300 mb-2">
                <strong>Webhook:</strong> {webhook.name}
              </div>
              <div className="text-slate-300 mb-2">
                <strong>Event Type:</strong> {webhook.eventType}
              </div>
              <div className="text-slate-400 text-sm bg-slate-900 p-3 rounded-lg break-all">
                {webhook.webhookUrl}
              </div>
            </div>

            {result && (
              <div
                className={`p-4 rounded-lg ${
                  result.success
                    ? 'bg-green-900/20 border border-green-500 text-green-400'
                    : 'bg-red-900/20 border border-red-500 text-red-400'
                }`}
              >
                {result.success ? '✅' : '❌'} {result.message}
              </div>
            )}

            <div className="bg-orange-900/20 border border-blue-500 rounded-lg p-4">
              <p className="text-orange-400 text-sm">
                <strong>ℹ️ Info:</strong> This will send a test message to your
                webhook URL with sample data for the "{webhook.eventType}" event
                type.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 p-4 rounded-b-lg flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg font-medium transition-colors"
            disabled={loading}
          >
            Close
          </button>
          <button
            onClick={handleTest}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Test Webhook'}
          </button>
        </div>
      </div>
    </div>
  );
};
